import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import SocketV1Service from '../services/socket/socket-v1.service';

interface UseSocketV1Options {
  autoConnect?: boolean;
  reconnectOnMount?: boolean;
  token?: string;
  query?: Record<string, any>;
  auth?: Record<string, any>;
}

interface UseSocketV1Return {
  emit: (event: string, ...args: any[]) => void;
  on: (event: string, handler: (data: any) => void) => void;
  once: (event: string, handler: (data: any) => void) => void;
  off: (event: string, handler?: (...args: any[]) => void) => void;
  isConnected: boolean;
  socketId: string | undefined;
  connect: () => void;
}

export function useSocketV1(
  url?: string,
  options: UseSocketV1Options = {}
): UseSocketV1Return {
  const { 
    autoConnect = true, 
    reconnectOnMount = false,
    token,
    query,
    auth,
  } = options;
  
  const [isConnected, setIsConnected] = useState(false);
  const [socketId, setSocketId] = useState<string | undefined>(undefined);
  
  const socketServiceRef = useRef<SocketV1Service | null>(null);
  const isServiceReadyRef = useRef<boolean>(false);
  const handlersRef = useRef<Map<string, Array<(...args: any[]) => void>>>(
    new Map()
  );

  // Serializa query e auth para comparação estável nas dependências
  const queryString = useMemo(() => JSON.stringify(query), [query]);
  const authString = useMemo(() => JSON.stringify(auth), [auth]);

  useEffect(() => {
    if (!url) return;

    try {
      const queryParams: Record<string, any> = { ...query };
      const authParams: Record<string, any> = { ...auth };
      if (token) {
        authParams.token = token;
      }

      socketServiceRef.current = SocketV1Service.getInstance({
        url,
        options: {
          transports: ['websocket', 'polling'] as Array<'websocket' | 'polling'>,
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          reconnectionAttempts: 5,
          timeout: 10000,
          pingTimeout: 60000,
          pingInterval: 25000,
          ...(Object.keys(queryParams).length > 0 && { query: queryParams }),
          ...(Object.keys(authParams).length > 0 && { auth: authParams }),
        },
      });

      isServiceReadyRef.current = true;

      if (autoConnect || reconnectOnMount) {
        socketServiceRef.current.connect();
      }
    } catch (error) {
      console.error('[useSocketV1] Initialization error:', error);
      isServiceReadyRef.current = false;
    }

    const handlers = handlersRef.current;
    const socketService = socketServiceRef.current;

    return () => {
      isServiceReadyRef.current = false;
      handlers.forEach((handlerList, event) => {
        handlerList.forEach(handler => {
          socketService?.off(event, handler);
        });
      });
      handlers.clear();
    };
  }, [url, autoConnect, reconnectOnMount, token, queryString, authString, query, auth]);

  
  const emit = useCallback((event: string, ...args: any[]) => {
    socketServiceRef.current?.emit(event, ...args);
  }, []);


  const on = useCallback((event: string, handler: (data: any) => void) => {
    if (!isServiceReadyRef.current || !socketServiceRef.current) {
      console.warn('[useSocketV1] Cannot add listener - service not ready or not initialized');
      return;
    }

    if (!handlersRef.current.has(event)) {
      handlersRef.current.set(event, []);
    }
    handlersRef.current.get(event)?.push(handler);

    socketServiceRef.current.on(event, handler);
  }, []);

  const once = useCallback(
    (event: string, handler: (data: any) => void) => {
      socketServiceRef.current?.once(event, handler);
    },
    []
  );

  const off = useCallback(
    (event: string, handler?: (...args: any[]) => void) => {
      if (handler) {
        const handlers = handlersRef.current.get(event);
        if (handlers) {
          const index = handlers.indexOf(handler);
          if (index > -1) {
            handlers.splice(index, 1);
          }
          if (handlers.length === 0) {
            handlersRef.current.delete(event);
          }
        }
      } else {
        handlersRef.current.delete(event);
      }

      socketServiceRef.current?.off(event, handler);
    },
    []
  );

  const connect = useCallback(() => {
    socketServiceRef.current?.connect();
  }, []);

  useEffect(() => {
    if (!socketServiceRef.current) return;

    const updateConnectionState = () => {
      setIsConnected(socketServiceRef.current?.isConnected() ?? false);
      setSocketId(socketServiceRef.current?.getSocketId());
    };

    const handleConnect = () => {
      updateConnectionState();
    };

    const handleDisconnect = () => {
      updateConnectionState();
    };

    socketServiceRef.current.addEventListener('connect', handleConnect);
    socketServiceRef.current.addEventListener('disconnect', handleDisconnect);

    updateConnectionState();

    return () => {
      socketServiceRef.current?.removeEventListener('connect', handleConnect);
      socketServiceRef.current?.removeEventListener('disconnect', handleDisconnect);
    };
  }, []);

  return {
    emit,
    on,
    once,
    off,
    isConnected,
    socketId,
    connect,
  };
}

export function useSocketV1Instance(): UseSocketV1Return {
  const [isConnected, setIsConnected] = useState(false);
  const [socketId, setSocketId] = useState<string | undefined>(undefined);
  const [isInstanceAvailable, setIsInstanceAvailable] = useState(false);
  
  const socketServiceRef = useRef<SocketV1Service | null>(null);
  const handlersRef = useRef<Map<string, Array<(...args: any[]) => void>>>(
    new Map()
  );

  useEffect(() => {
    const tryGetInstance = () => {
      try {
        socketServiceRef.current = SocketV1Service.getInstance();
        setIsInstanceAvailable(true);
        return true;
      } catch (error) {
        // Instância ainda não foi criada
        setIsInstanceAvailable(false);
        return false;
      }
    };

    // Se não conseguiu, tenta novamente a cada 500ms
    const interval = setInterval(() => {
      if (tryGetInstance()) {
        clearInterval(interval);
      }
    }, 500);

    // Captura os valores para o cleanup
    const handlers = handlersRef.current;
    const socketService = socketServiceRef.current;

    return () => {
      clearInterval(interval);
      handlers.forEach((handlerList, event) => {
        handlerList.forEach(handler => {
          socketService?.off(event, handler);
        });
      });
      handlers.clear();
    };
  }, []);

  useEffect(() => {
    if (!isInstanceAvailable || !socketServiceRef.current) return;

    const updateConnectionState = () => {
      setIsConnected(socketServiceRef.current?.isConnected() ?? false);
      setSocketId(socketServiceRef.current?.getSocketId());
    };

    const handleConnect = () => {
      updateConnectionState();
    };

    const handleDisconnect = () => {
      updateConnectionState();
    };

    socketServiceRef.current.addEventListener('connect', handleConnect);
    socketServiceRef.current.addEventListener('disconnect', handleDisconnect);

    updateConnectionState();

    return () => {
      socketServiceRef.current?.removeEventListener('connect', handleConnect);
      socketServiceRef.current?.removeEventListener('disconnect', handleDisconnect);
    };
  }, [isInstanceAvailable]);

  const emit = useCallback((event: string, ...args: any[]) => {
    socketServiceRef.current?.emit(event, ...args);
  }, []);


  const on = useCallback((event: string, handler: (data: any) => void) => {
    if (!socketServiceRef.current) {
      console.warn('[useSocketV1Instance] Cannot add listener - service not initialized');
      return;
    }

    if (!handlersRef.current.has(event)) {
      handlersRef.current.set(event, []);
    }
    handlersRef.current.get(event)?.push(handler);

    socketServiceRef.current.on(event, handler);
    }, []);

  const once = useCallback(
    (event: string, handler: (data: any) => void) => {
      socketServiceRef.current?.once(event, handler);
    },
    []
  );

  const off = useCallback(
    (event: string, handler?: (...args: any[]) => void) => {
      if (handler) {
        const handlers = handlersRef.current.get(event);
        if (handlers) {
          const index = handlers.indexOf(handler);
          if (index > -1) {
            handlers.splice(index, 1);
          }
          if (handlers.length === 0) {
            handlersRef.current.delete(event);
          }
        }
      } else {
        handlersRef.current.delete(event);
      }

      socketServiceRef.current?.off(event, handler);
    },
    []
  );

  const connect = useCallback(() => {
    socketServiceRef.current?.connect();
  }, []);

  return {
    emit,
    on,
    once,
    off,
    isConnected,
    socketId,
    connect,
  };
}
